<template>
  <page-layout>
    <template #content>
      <div class="mt-12 pb-8">
        <div class="max-w-3xl mx-auto px-4 sm:px-6 lg:max-w-7xl lg:px-8">
          <!-- Main 3 column grid -->
          <div
            class="grid grid-cols-1 gap-4 items-start lg:grid-cols-3 lg:gap-8"
          >
            <!-- Left column -->
            <div class="grid grid-cols-1 gap-4 lg:col-span-2">
              <!-- Welcome panel -->
              <section aria-labelledby="profile-overview-title">
                <div class="rounded-lg bg-white overflow-hidden shadow">
                  <h2 class="sr-only" id="profile-overview-title">
                    Profile Overview
                  </h2>
                  <div
                    class="
                      bg-gradient-to-r
                      from-blue-500
                      via-indigo-500
                      to-purple-500
                      p-6
                    "
                  >
                    <div class="sm:flex sm:items-center sm:justify-between">
                      <div class="sm:flex sm:space-x-5">
                        <div class="flex-shrink-0">
                          <div class="mx-auto flex justify-center">
                            <ui-avatar
                              backgroundStyling="bg-white"
                              textStyling="text-light-blue-400"
                              sizeStyling="h-20 w-20"
                            />
                          </div>
                        </div>
                        <div
                          class="mt-4 text-center sm:mt-0 sm:pt-1 sm:text-left"
                        >
                          <p class="text-sm font-medium text-light-blue-100">
                            {{ t("welcome_back") }}
                          </p>
                          <p class="text-xl font-bold text-white sm:text-2xl">
                            {{ $page.props.user.data.name }}
                          </p>
                          <p class="text-sm font-medium text-light-blue-100">
                            {{ t($page.props.user.data.account.type) }}
                          </p>
                        </div>
                      </div>
                      <div class="mt-5 flex justify-center sm:mt-0">
                        <Link
                          :href="route('profile')"
                          class="
                            flex
                            justify-center
                            items-center
                            px-4
                            py-2
                            border border-gray-300
                            shadow-sm
                            text-sm
                            font-medium
                            rounded-md
                            text-gray-700
                            bg-white
                            hover:bg-gray-50
                          "
                        >
                          {{ t("profile") }}
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <!-- Actions panel -->
              <section aria-labelledby="quick-links-title">
                <div
                  class="
                    rounded-lg
                    bg-gray-200
                    overflow-hidden
                    shadow
                    divide-y divide-gray-200
                    sm:divide-y-0 sm:grid sm:grid-cols-2 sm:gap-px
                  "
                >
                  <div
                    v-for="(menuItem, index) in menuItems"
                    :key="index"
                    :class="'focus-within:ring-' + menuItem.color + '-500'"
                    class="
                      relative
                      group
                      bg-white
                      p-6
                      focus-within:ring-2 focus-within:ring-inset
                    "
                  >
                    <div>
                      <span
                        :class="menuItem.iconBackgroundColor"
                        class="rounded-lg inline-flex p-3 ring-4 ring-white"
                      >
                        <component
                          class="h-6 w-6"
                          :class="menuItem.iconColor"
                          :is="menuItem.iconComponent"
                        />
                      </span>
                    </div>
                    <div class="mt-8">
                      <h3 class="text-lg font-medium">
                        <Link :href="menuItem.route" class="focus:outline-none">
                          <span
                            class="absolute inset-0"
                            aria-hidden="true"
                          ></span>
                          {{ menuItem.title }}
                        </Link>
                      </h3>
                      <p class="mt-2 text-sm text-gray-500">
                        {{ menuItem.description }}
                      </p>
                    </div>
                    <span
                      :class="menuItem.arrowHoverColor"
                      class="absolute top-6 right-6 text-gray-300"
                      aria-hidden="true"
                    >
                      <svg
                        class="h-6 w-6"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          d="M20 4h1a1 1 0 00-1-1v1zm-1 12a1 1 0 102 0h-2zM8 3a1 1 0 000 2V3zM3.293 19.293a1 1 0 101.414 1.414l-1.414-1.414zM19 4v12h2V4h-2zm1-1H8v2h12V3zm-.707.293l-16 16 1.414 1.414 16-16-1.414-1.414z"
                        />
                      </svg>
                    </span>
                  </div>
                </div>
              </section>

              <section aria-labelledby="recent-hires-title">
                <div class="rounded-lg bg-white overflow-hidden shadow">
                  <div class="p-6">
                    <h2
                      class="text-base font-medium text-gray-900"
                      id="recent-hires-title"
                    >
                      {{ t("recent_quotations") }}
                    </h2>
                    <div class="flow-root mt-6">
                      <ul class="-my-5 divide-y divide-gray-200">
                        <li
                          class="py-4"
                          v-for="quotation in quotations.data"
                          :key="quotation.id"
                        >
                          <div class="flex items-center space-x-4">
                            <div class="flex-1 min-w-0">
                              <p
                                class="
                                  text-sm
                                  font-medium
                                  text-gray-900
                                  truncate
                                "
                              >
                                {{ quotation.id }}
                              </p>
                              <p
                                v-if="quotation.customer"
                                class="text-sm text-gray-500 truncate"
                              >
                                {{ quotation.customer.name }}
                              </p>
                            </div>
                            <div>
                              <Link
                                :href="
                                  route('quotations.show', {
                                    quotation: quotation.id,
                                  })
                                "
                                class="
                                  inline-flex
                                  items-center
                                  shadow-sm
                                  px-2.5
                                  py-0.5
                                  border border-gray-300
                                  text-sm
                                  leading-5
                                  font-medium
                                  rounded-full
                                  text-gray-700
                                  bg-white
                                  hover:bg-gray-50
                                "
                              >
                                {{ t("view") }}
                              </Link>
                            </div>
                          </div>
                        </li>
                        <li class="py-4" v-if="quotations.data.length == 0">
                          <div class="flex items-center space-x-4">
                            <div class="flex-1 min-w-0">
                              <p
                                class="
                                  text-sm
                                  font-medium
                                  text-gray-900
                                  truncate
                                "
                              >
                                {{ t("no_quotations") }}
                              </p>
                            </div>
                            <div>
                              <Link
                                :href="route('quotations.create')"
                                class="
                                  inline-flex
                                  items-center
                                  shadow-sm
                                  px-2.5
                                  py-0.5
                                  border border-gray-300
                                  text-sm
                                  leading-5
                                  font-medium
                                  rounded-full
                                  text-gray-700
                                  bg-white
                                  hover:bg-gray-50
                                "
                              >
                                {{ t("quotation_create") }}
                              </Link>
                            </div>
                          </div>
                        </li>
                      </ul>
                    </div>
                    <div class="mt-6" v-if="quotations.data.length">
                      <Link
                        :href="route('quotations.index')"
                        class="
                          w-full
                          flex
                          justify-center
                          items-center
                          px-4
                          py-2
                          border border-gray-300
                          shadow-sm
                          text-sm
                          font-medium
                          rounded-md
                          text-gray-700
                          bg-white
                          hover:bg-gray-50
                        "
                      >
                        {{ t("view_all") }}
                      </Link>
                    </div>
                  </div>
                </div>
              </section>
            </div>

            <!-- Right column -->
            <div class="grid grid-cols-1 gap-4">
              <!-- Announcements -->
              <section aria-labelledby="announcements-title">
                <div class="rounded-lg bg-white overflow-hidden shadow">
                  <div class="p-6">
                    <h2
                      class="text-base font-medium text-gray-900"
                      id="announcements-title"
                    >
                      {{ t("equipments_with_calibrations_due") }}
                    </h2>
                    <div class="flow-root mt-6">
                      <ul class="-my-5 divide-y divide-gray-200">
                        <li
                          class="py-5"
                          v-for="equipment in equipments.data"
                          :key="equipment.id"
                        >
                          <div
                            class="
                              relative
                              focus-within:ring-2
                              focus-within:ring-light-blue-500
                            "
                          >
                            <h3 class="text-sm font-semibold text-gray-800">
                              <Link
                                :href="
                                  route('equipment.edit', {
                                    equipment: equipment.id,
                                  })
                                "
                                class="hover:underline focus:outline-none"
                              >
                                <!-- Extend touch target to entire panel -->
                                <span
                                  class="absolute inset-0"
                                  aria-hidden="true"
                                ></span>
                                {{ equipment.identifier }}
                              </Link>
                            </h3>
                            <span
                              :class="{
                                'text-red-400': equipment.expired_calibration,
                              }"
                              class="text-xs"
                              >{{
                                equipment.formatted_next_calibration_on
                              }}</span
                            >
                            <p class="mt-1 text-xs text-gray-600 line-clamp-2">
                              {{
                                t("serial_no", {
                                  serial: equipment.serial_number,
                                })
                              }}
                            </p>
                            <p class="mt-1 text-xs text-gray-600 line-clamp-2">
                              {{ t("model", { model: equipment.model }) }}
                            </p>
                          </div>
                        </li>
                        <li class="py-5" v-if="equipments.data.length == 0">
                          <div
                            class="
                              relative
                              focus-within:ring-2
                              focus-within:ring-light-blue-500
                            "
                          >
                            <span
                              class="text-sm text-gray-500 flex items-center"
                            >
                              Todos los equipos en orden
                              <CheckCircleIcon
                                class="ml-1 h-5 w-5 text-gray-500"
                              />
                            </span>
                          </div>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </template>
  </page-layout>
</template>

<script>
import {
  DocumentTextIcon,
  UserGroupIcon,
  UsersIcon,
  DesktopComputerIcon,
  CheckCircleIcon,
} from "@heroicons/vue/outline";
import AppLayout from "./../Layouts/AppLayout";
import PageLayout from "./../Layouts/PageLayout";
import { useI18n } from "vue-i18n";

export default {
  layout: AppLayout,
  components: {
    AppLayout,
    PageLayout,
    DocumentTextIcon,
    UserGroupIcon,
    UsersIcon,
    DesktopComputerIcon,
    CheckCircleIcon,
  },
  props: ["equipments", "quotations"],
  data() {
    return {
      menuItems: [
        {
          arrowHoverColor: "group-hover:text-green-400",
          iconBackgroundColor: "text-green-50",
          iconColor: "text-green-700",
          iconComponent: "DocumentTextIcon",
          route: this.route("quotations.index"),
          title: this.t("quotations"),
          description: this.t("quotations_description"),
        },
        {
          arrowHoverColor: "group-hover:text-indigo-400",
          iconBackgroundColor: "text-indigo-50",
          iconColor: "text-indigo-700",
          iconComponent: "UserGroupIcon",
          route: this.route("users.index"),
          title: this.t("personnel"),
          description: this.t("personnel_description"),
        },
        {
          arrowHoverColor: "group-hover:text-red-400",
          iconBackgroundColor: "text-red-50",
          iconColor: "text-red-700",
          iconComponent: "UsersIcon",
          route: this.route("customers.index"),
          title: this.t("customers"),
          description: this.t("customers_description"),
        },
        {
          arrowHoverColor: "group-hover:text-yellow-400",
          iconBackgroundColor: "text-yellow-50",
          iconColor: "text-yellow-700",
          iconComponent: "DesktopComputerIcon",
          route: this.route("equipment.index"),
          title: this.t("equipment"),
          description: this.t("equipment_description"),
        },
      ],
    };
  },
  setup() {
    const { t } = useI18n();
    return { t };
  },
};
</script>

<i18n>
{
  "es": {
    "view": "Ver",
    "view_all": "Ver todas",
    "welcome_back": "Bienvenido,",
    "administrator": "Administrador",
    "employee": "Empleado",
    "technician": "Signatario",
    "recent_quotations": "Cotizaciones recientes",
    "no_quotations": "No hay cotizaciones",
    "quotation_create": "Crear cotización",
    "quotations": "Cotizaciones",
    "quotations_description": "Crea y revisa las cotizaciones para los clientes.",
    "personnel": "Personal",
    "personnel_description": "Ver y administrar el personal junto con sus permisos.",
    "customers": "Clientes",
    "customers_description": "Ver y administrar los clientes y su información como contactos y cotizaciones.",
    "equipment": "Equipos de medición",
    "equipment_description": "Ver y administrar los equipos y su información como calibraciones y revisiones.",
    "model": "Model: {model}",
    "serial_no": "Serial number: {serial}",
    "technician": "Signatario",
    "equipments_with_calibrations_due": "Equipos con calibraciones próximas",
    "profile": "Ver perfil"
  }
}
</i18n>